import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, Phone, Mail, MapPin, Clock } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";

export default function Footer() {
  const { settings } = useSite();
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-border/60 bg-secondary/40 mt-24">
      <div className="gold-divider" />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src={settings.logo}
                alt="Hermanos"
                loading="lazy"
                decoding="async"
                className="h-11 w-auto"
                onError={(e) => {
                  const fallback = "https://customer-assets.emergentagent.com/job_bf956fe1-1570-4257-80a8-b41c4eebcfc0/artifacts/pp6j9f5w_LOGO.png";
                  if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                }}
              />
              <div className="flex flex-col leading-tight">
                <span className="font-serif text-xl text-foreground">Hermanos</span>
                <span className="text-[0.6rem] tracking-[0.24em] uppercase text-gold font-semibold">Industries Pvt Ltd</span>
              </div>
            </Link>
            <p className="text-sm text-foreground/70 leading-relaxed max-w-xs">
              India's trusted B2B wholesale readymade garment supplier. Bulk supply, 40+ brands, pan-India delivery.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {settings.social_instagram ? (
                <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-sm border border-border/60 flex items-center justify-center hover:border-gold/60 hover:text-gold transition-colors text-foreground/70" aria-label="Instagram">
                  <Instagram size={15} />
                </a>
              ) : null}
              {settings.social_facebook ? (
                <a href={settings.social_facebook} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-sm border border-border/60 flex items-center justify-center hover:border-gold/60 hover:text-gold transition-colors text-foreground/70" aria-label="Facebook">
                  <Facebook size={15} />
                </a>
              ) : null}
              {settings.social_linkedin ? (
                <a href={settings.social_linkedin} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-sm border border-border/60 flex items-center justify-center hover:border-gold/60 hover:text-gold transition-colors text-foreground/70" aria-label="LinkedIn">
                  <Linkedin size={15} />
                </a>
              ) : null}
              {settings.social_youtube ? (
                <a href={settings.social_youtube} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-sm border border-border/60 flex items-center justify-center hover:border-gold/60 hover:text-gold transition-colors text-foreground/70" aria-label="YouTube">
                  <Youtube size={15} />
                </a>
              ) : null}
            </div>
          </div>

          <div>
            <h4 className="section-label mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              {[["/about","About"],["/products","Products"],["/brands","Brands"],["/categories","Categories"],["/gallery","Gallery"],["/blogs","Journal"],["/faq","FAQ"]].map(([to, label]) => (
                <li key={to}><Link to={to} className="text-foreground/70 hover:text-gold transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="section-label mb-5">Business</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/request-catalogue" className="text-foreground/70 hover:text-gold transition-colors">Request Catalogue</Link></li>
              <li><Link to="/request-quotation" className="text-foreground/70 hover:text-gold transition-colors">Request Quotation</Link></li>
              <li><Link to="/contact" className="text-foreground/70 hover:text-gold transition-colors">Contact Sales</Link></li>
              <li><a href={`https://wa.me/${settings.whatsapp?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-foreground/70 hover:text-gold transition-colors">WhatsApp Business</a></li>
              <li><Link to="/admin/login" className="text-foreground/50 hover:text-gold/80 transition-colors text-xs tracking-wider uppercase">Admin Portal</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="section-label mb-5">Reach Us</h4>
            <ul className="space-y-3 text-sm text-foreground/70">
              <li className="flex gap-3"><MapPin size={16} className="mt-0.5 text-gold shrink-0" /><span>{settings.address}</span></li>
              <li className="flex gap-3"><Phone size={16} className="mt-0.5 text-gold shrink-0" /><a href={`tel:${settings.phone_primary}`} className="hover:text-gold">{settings.phone_primary}</a></li>
              {settings.phone_secondary ? (
                <li className="flex gap-3 pl-7"><a href={`tel:${settings.phone_secondary}`} className="hover:text-gold">{settings.phone_secondary}</a></li>
              ) : null}
              <li className="flex gap-3"><Mail size={16} className="mt-0.5 text-gold shrink-0" /><a href={`mailto:${settings.email}`} className="hover:text-gold break-all">{settings.email}</a></li>
              <li className="flex gap-3"><Clock size={16} className="mt-0.5 text-gold shrink-0" /><span>{settings.business_hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-border/60 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-foreground/60">
          <p>© {year} Hermanos Industries Pvt Ltd. All rights reserved.</p>
          <p className="tracking-wider uppercase text-[0.68rem]">
            Wholesale • Bulk Supply • Pan India • No Retail
          </p>
        </div>
      </div>
    </footer>
  );
}
